import { FormController, FormInput, FormTextArea } from '@/components/form';
import { ProcurementApi } from '@/state/api';
import { ProcurementType } from '@/types';
import { Box, Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { EmptyState, toasterApi } from '@ui';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';

type EditDraftOrderLineProps = {
  lineId?: number;
  draftOrder?: ProcurementType.DraftOrders.GetDraftOrder['response'];
  isEditEnabled?: boolean;
};

const EditDraftOrderLine = ({
  draftOrder,
  lineId,
  isEditEnabled = false,
}: EditDraftOrderLineProps) => {
  // Get current line
  const currentLine = draftOrder?.lines?.find((line) => line.id === lineId);

  const partId = (currentLine as any)?.part_id ?? null;
  const partNumber = String((currentLine as any)?.part_number ?? '').trim();
  const isManualLine = !partId && !!partNumber;

  // local editable value for manual part name only
  const [manualPartName, setManualPartName] = useState<string>(partNumber);

  useEffect(() => {
    setManualPartName(partNumber);
  }, [currentLine?.id, partNumber]);

  // Patch line
  const [patchDraftOrderLineMutation] =
    ProcurementApi.Api.usePatchDraftOrderLineMutation();

  return (
    <Flex flexDirection="column" gapY={4}>
      <Flex flexDirection="column" gapY={1}>
        <Text fontSize="sm" fontWeight="bold">
          Line Details
        </Text>
        {lineId && (
          <Flex fontWeight="bold" alignItems="center">
            <Text>Line {lineId}</Text>
          </Flex>
        )}
      </Flex>

      <FormController
        disabled={!isEditEnabled}
        key={`edit-draft-order-line-${currentLine?.id ?? 'none'}`}
        size="lg"
        formSchema={ProcurementType.DraftOrderLines.patch.body}
        defaultValues={{
          draft: currentLine?.draft,
          qty: currentLine?.qty,
          unit_price: currentLine?.unit_price,
          needed_by: dayjs(currentLine?.needed_by).format('YYYY-MM-DD'),
          notes: currentLine?.notes,
        }}
        onFormSubmit={(formData) => {
          // Build payload that works for BOTH system + manual
          const body: any = {
            qty: formData.qty,
            unit_price: formData.unit_price,
            needed_by: formData.needed_by,
            notes: formData.notes,
          };

          if (isManualLine) {
            body.manual_part_number =
              String(manualPartName || '').trim() || null;
            body.part = null;
          } else {
            body.part = partId;
            body.manual_part_number = null;
          }

          patchDraftOrderLineMutation({
            body,
            params: { id: currentLine?.id! },
          })
            .unwrap()
            .then(() => {
              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'Draft order line updated successfully',
              });
            })
            .catch(() => {
              toasterApi({
                type: 'error',
                status: 500,
                code: 'Error',
                message: 'Could not update draft order line',
              });
            });
        }}
        buttonProps={{
          hidden: true,
        }}
      >
        {({
          register,
          control,
          errors,
        }: {
          register: UseFormRegister<
            ProcurementType.DraftOrderLines.Patch['body']
          >;
          control: Control<ProcurementType.DraftOrderLines.Patch['body']>;
          errors: FieldErrors<ProcurementType.DraftOrderLines.Patch['body']>;
        }) => {
          return (
            <Flex flexDirection="column" gapY={4} overflowX="hidden">
              <Flex flexDirection="column">
                <Flex paddingBottom={4} gapX={4} alignItems="center">
                  {!!draftOrder?.lines?.length && (
                    <Grid
                      templateColumns="repeat(4, 1fr)"
                      gap={4}
                      alignItems="end"
                      width="full"
                    >
                      {/* Part (editable only for manual lines) */}
                      <GridItem>
                        <FormInput
                          width="full"
                          register={register}
                          control={control}
                          errors={errors}
                          id="null" // not used
                          label="Part"
                          showLabelAtTop
                          onChange={(e) => setManualPartName(e.target.value)}
                          placeholder="Manual Part Number"
                          value={
                            isManualLine ? manualPartName : partNumber || '—'
                          }
                          disabled={!isManualLine}
                        />
                      </GridItem>

                      {/* Needed by */}
                      <GridItem>
                        <FormInput
                          width="full"
                          register={register}
                          control={control}
                          errors={errors}
                          id="needed_by"
                          label="Needed By"
                          showLabelAtTop
                          type="date"
                        />
                      </GridItem>

                      {/* Quantity */}
                      <GridItem>
                        <FormInput
                          width="full"
                          defaultValue={currentLine?.qty ?? 1}
                          register={register}
                          control={control}
                          errors={errors}
                          id="qty"
                          label="Quantity"
                          showLabelAtTop
                          min={1}
                          type="number"
                        />
                      </GridItem>

                      {/* Unit Price */}
                      <GridItem>
                        <Box width="200px">
                          <FormInput
                            width="200px"
                            register={register}
                            control={control}
                            errors={errors}
                            id="unit_price"
                            label="Unit Price"
                            showLabelAtTop
                            type="number"
                            min={0}
                            step="0.01"
                          />
                        </Box>
                      </GridItem>

                      {/* Notes */}
                      <GridItem colSpan={4}>
                        <FormTextArea
                          register={register}
                          control={control}
                          errors={errors}
                          id="notes"
                          label="Notes (Optional)"
                          showLabelAtTop
                          minH="2lh"
                          maxH="2lh"
                        />
                      </GridItem>
                    </Grid>
                  )}
                </Flex>
              </Flex>

              {draftOrder?.lines?.length === 0 && (
                <EmptyState
                  isEmpty
                  title="No lines added to the draft"
                  description="Please add minimum one item as a line to the current draft order."
                  position="relative"
                />
              )}

              <Flex justifyContent="end" gapX={2}>
                {lineId && (
                  <Button
                    variant="outline"
                    color="fg"
                    size="sm"
                    borderColor="fg"
                    type="submit"
                  >
                    Save Line
                  </Button>
                )}
              </Flex>
            </Flex>
          );
        }}
      </FormController>
    </Flex>
  );
};

export default EditDraftOrderLine;
